import { useTranslation } from 'react-i18next';

import type { TablePaginationProps } from '@mui/material/TablePagination';
import TablePagination from '@mui/material/TablePagination';

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

export interface CompactTablePaginationProps
  extends Omit<TablePaginationProps, 'component' | 'rowsPerPageOptions' | 'labelRowsPerPage'> {
  rowsPerPageOptions?: TablePaginationProps['rowsPerPageOptions'];
  labelRowsPerPage?: string;
  hideRowsPerPage?: boolean;
}

/**
 * Compact footer pagination for dense back-office tables.
 *
 * Keeps the control line short and visually lightweight so it does not steal
 * vertical space from the table body. `labelRowsPerPage` defaults to the translated
 * `common:pagination.rowsPerPageLabel` key — pass an explicit value only to override it
 * for a specific screen (e.g. a screen-specific noun instead of the generic "rows").
 */
export function CompactTablePagination({
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  labelRowsPerPage,
  hideRowsPerPage = false,
  sx,
  ...props
}: CompactTablePaginationProps) {
  const { t } = useTranslation();
  const effectiveLabel = labelRowsPerPage ?? t('pagination.rowsPerPageLabel');
  const effectiveRowsPerPageOptions = hideRowsPerPage ? [] : [...rowsPerPageOptions];

  return (
    <TablePagination
      component="div"
      rowsPerPageOptions={effectiveRowsPerPageOptions}
      labelRowsPerPage={hideRowsPerPage ? '' : effectiveLabel}
      sx={{
        width: '100%',
        minHeight: 44,
        '& .MuiTablePagination-toolbar': {
          minHeight: 44,
          px: 0,
          pl: 0,
          pr: 0,
        },
        '& .MuiTablePagination-spacer': {
          flex: 'none',
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          my: 0,
        },
        ...(hideRowsPerPage
          ? {
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-input': {
                display: 'none',
              },
            }
          : {
              '& .MuiTablePagination-select': {
                py: 0.25,
              },
              '& .MuiTablePagination-input': {
                mr: 1,
              },
            }),
        '& .MuiTablePagination-actions': {
          ml: 1,
        },
        ...sx,
      }}
      {...props}
    />
  );
}
